import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { BATTLES_STANDING_TYPE, BATTLES_TABS } from 'shared/screens/Battles/constants/dictionary';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import styled, { css } from 'styled-components';
import useLeaderBoardTableSizeCalculator from '../hooks/useLeaderBoardTableSizeCalculator';
import { truncatePlayerName } from '../utils';
import { isStandalonePWA } from '../../../utils/mobileSupported';
const BattlesRankingTable = ({ battlesAssets, isPlayerJoining, tableData, selectedTab, scaleRef }) => {
  const { containerStyle } = useLeaderBoardTableSizeCalculator(scaleRef);

  const columns = useMemo(
    () =>
      [
        {
          header: getText(TEXT_KEY.BATTLES_RANK),
          accessorKey: 'rank',
          cell: ({ row, getValue }) =>
            isPlayerJoining && !row.original.hasJoined ? getText(TEXT_KEY.BATTLES_DASH) : getValue(),
        },
        {
          header: getText(TEXT_KEY.BATTLES_PLAYER),
          accessorKey: 'name',

          cell: ({ row }) => (
            <PlayerText id={row.original.id}>
              {isPlayerJoining && !row.original.hasJoined ? (
                <>
                  {getText(TEXT_KEY.BATTLES_JOINING)}
                  {'...'.split('').map((char, index) => (
                    <AnimatedLetter key={index} delay={index * 0.1}>
                      {char}
                    </AnimatedLetter>
                  ))}
                </>
              ) : (
                truncatePlayerName(row.original.name)
              )}
            </PlayerText>
          ),
        },
        {
          header: getText(TEXT_KEY.BATTLES_POINTS),
          accessorKey: 'points',
          cell: ({ row, getValue }) => (
            <Points index={row.index}>
              {row.index < 3 ? (
                <PointsIcon src={battlesAssets.BATTLES_POINTS_ICON_BATTLES} alt="points-icon" />
              ) : (
                <EmptyBox />
              )}
              {isPlayerJoining && !row.original.hasJoined ? getText(TEXT_KEY.BATTLES_DASH) : getValue()}
            </Points>
          ),
        },
        selectedTab !== BATTLES_TABS.STREAK && {
          header: getText(TEXT_KEY.BATTLES_PROGRESS),
          accessorKey: 'progress',
          cell: ({ row, getValue }) =>
            isPlayerJoining && !row.original.hasJoined ? getText(TEXT_KEY.BATTLES_DASH) : `${getValue() ?? 0}%`,
        },
      ].filter(Boolean),
    [isPlayerJoining, battlesAssets, tableData]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Container
      style={{
        '--scale-factor': containerStyle.scale,
        marginTop: containerStyle.marginTop,
        opacity: containerStyle.opacity,
      }}
    >
      {table.getHeaderGroups().map(headerGroup => (
        <LeaderboardHeader key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => (
            <TableHeaderItem index={index} key={header.id}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </TableHeaderItem>
          ))}
        </LeaderboardHeader>
      ))}
      {table.getRowModel().rows.map(row => {
        const isPlayer = row.original.type === BATTLES_STANDING_TYPE.PLAYER;
        return (
          <Row key={row.id} highlight={isPlayer}>
            {row.getVisibleCells().map((cell, index) => (
              <TableCellItemWrapper key={cell.id} index={index} highlight={isPlayer}>
                <TableDataCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableDataCell>
              </TableCellItemWrapper>
            ))}
          </Row>
        );
      })}
    </Container>
  );
};

export default BattlesRankingTable;

const LeaderboardHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  padding: 0;
`;

const ContainerPWA = css`
  justify-content: space-between;
  height: 69%;

  ${({ theme }) => theme.screens.device('mobile-standalone') && theme.platform('android') && `height: 74%;`}
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  transform: scale(var(--scale-factor));
  ${isStandalonePWA && ContainerPWA}
`;

const TableHeaderItem = styled.div.withConfig({
  shouldForwardProp: prop => !['index'].includes(prop),
})`
  flex: ${({ index }) => FlexSizeMap[index]};
  display: flex;
  justify-content: center;
  font-weight: 700;
  font-size: 2vw;
  text-transform: uppercase;

  ${({ theme }) => theme.BattlesRankingTable.TableHeaderItem}
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) && theme.platform('android') && 'font-size: 1.6vw'}
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) && theme.platform('ios') && 'font-size: 1.35vw'}
`;

const Row = styled.div.withConfig({
  shouldForwardProp: prop => !['highlight'].includes(prop),
})`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ highlight }) => (highlight ? '#f9b233' : 'transparent')};
  border-radius: ${({ highlight }) => (highlight ? '50px' : '6px')};
  font-weight: ${({ highlight }) => (highlight ? 'bold' : 'normal')};
  color: ${({ highlight }) => (highlight ? '#470702' : '#fff')};
`;

const TableCellItemWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['index', 'highlight'].includes(prop),
})`
  flex: ${({ index }) => FlexSizeMap[index]};
  display: flex;
  justify-content: ${({ index }) => (index === 1 ? 'flex-start' : 'center')};
  color: ${({ highlight }) => (highlight ? '#470702' : '#fff')};
  font-size: 18px;
`;

const TableDataCell = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 1.8vw;
  ${({ theme }) => theme.screens.devices(['mobile-browser', 'mobile-standalone']) && 'font-size: 1.3rem'}
`;

const Points = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5vw;
  justify-content: flex-start;
  min-width: 5vw;
  ${({ theme }) => theme.screens.devices(['mobile-browser', 'mobile-standalone']) && 'min-width: 3vw'}
`;

const PointsIcon = styled.img`
  object-fit: contain;
  width: 2.5vw;
  ${({ theme }) => theme.screens.devices(['mobile-browser', 'mobile-standalone']) && 'width: 1.6vw'}
`;

const EmptyBox = styled.div`
  width: 2.5vw;
  ${({ theme }) => theme.screens.devices(['mobile-browser', 'mobile-standalone']) && 'width: 1.6vw'}
`;

const AnimatedLetter = styled.span.withConfig({
  shouldForwardProp: prop => !['delay'].includes(prop),
})`
  opacity: 0;
  animation: blink 2s infinite;
  animation-delay: ${({ delay }) => delay}s;
  display: inline-block;

  @keyframes blink {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const PlayerText = styled.p`
  text-align: initial;
  position: relative;
  left: 4vw;
  margin: 0;
  padding: 0;
  white-space: nowrap;

  ${({ theme }) => theme.screens.device('mobile-browser') && 'left: 2.8vw;'}

  ${({ theme }) => theme.BattlesRankingTable.LeaderboardPlayerText}
`;

const FlexSizeMap = {
  0: 0.7,
  1: 1.3,
  2: 0.8,
  3: 1.2,
};
